import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled
} from '@mui/material';
import { formatCurrency } from '../../utils/formatters';

// Interfaces
export interface BusinessDetails {
  name: string;
  address: string;
  phone: string;
  email: string;
  gstin?: string;
  gstRegistered?: 'registered' | 'not_registered';
  gstType?: 'regular' | 'composition' | '';
}

export interface ClientDetails {
  name: string;
  address: string;
  phone?: string;
  email?: string;
  gstin?: string;
}

export interface InvoiceItem {
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

export interface InvoiceDetails {
  invoiceNumber: string;
  invoiceDate: string;
  items: InvoiceItem[];
  discount?: number;
  additionalCharges?: {
    name: string;
    amount: number;
  }[];
  termsAndConditions?: string[];
  isInterstate?: boolean;
  copyLabel?: string;
  isCollectingCopy?: boolean;
}

export interface ThermalInvoiceFormatCompositionProps {
  businessDetails: BusinessDetails;
  clientDetails: ClientDetails;
  invoiceDetails: InvoiceDetails;
  widthPreset?: '3-inch' | '2-inch';
}

// Styled Components
const ThermalPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'widthPreset'
})<{ widthPreset?: '3-inch' | '2-inch' }>(({ theme, widthPreset }) => ({
  padding: theme.spacing(1),
  width: widthPreset === '2-inch' ? '58mm' : '80mm', // 2 inch or 3 inch thermal printer width
  maxWidth: '272px',
  margin: '0 auto',
  backgroundColor: '#fff',
  position: 'relative',
  fontFamily: 'Courier, monospace',
  fontSize: widthPreset === '2-inch' ? '8px' : '10px',
  '@media print': {
    width: widthPreset === '2-inch' ? '58mm' : '80mm',
    margin: 0,
    padding: theme.spacing(0),
    boxShadow: 'none'
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '& > *': {
    padding: '2px 6px',
    fontSize: '10px',
    lineHeight: '1.2',
    borderBottom: '1px dashed #ddd'
  }
}));

const StyledTableHeadRow = styled(TableRow)(({ theme }) => ({
  '& > *': {
    padding: '2px 6px',
    fontSize: '10px',
    fontWeight: 'bold',
    borderBottom: '1px solid #000'
  }
}));

// Column width definitions for proper alignment
const DescriptionCell = styled(TableCell)(({ theme }) => ({
  width: '45%', 
  maxWidth: '45%'
}));

const QuantityCell = styled(TableCell)(({ theme }) => ({
  width: '15%',
  maxWidth: '15%'
}));

const RateCell = styled(TableCell)(({ theme }) => ({
  width: '20%',
  maxWidth: '20%'
}));

const AmountCell = styled(TableCell)(({ theme }) => ({
  width: '20%',
  maxWidth: '20%'
}));

const ThermalInvoiceFormatComposition: React.FC<ThermalInvoiceFormatCompositionProps> = ({
  businessDetails,
  clientDetails,
  invoiceDetails,
  widthPreset = '3-inch'
}) => {
  // Calculate totals
  const calculateTotals = () => {
    const subtotal = invoiceDetails.items.reduce((acc, item) => acc + (item.rate * item.quantity), 0);

    let discount = invoiceDetails.discount || 0;

    const additionalChargesTotal = Array.isArray(invoiceDetails.additionalCharges)
      ? invoiceDetails.additionalCharges.reduce((sum, charge) => sum + (charge?.amount || 0), 0)
      : 0;

    const total = subtotal - discount + additionalChargesTotal;

    return {
      subtotal,
      discount,
      additionalChargesTotal,
      total
    };
  };

  const totals = calculateTotals();

  // Format date string to DD/MM/YYYY
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      return dateString; // Return as is if parsing fails
    }
  };

  return (
    <ThermalPaper widthPreset={widthPreset}>
      {invoiceDetails.copyLabel && (
        <Box sx={{ textAlign: 'center', mb: 1, borderBottom: '1px solid #000', pb: 0.5 }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>
            {invoiceDetails.copyLabel}
          </Typography>
        </Box>
      )}
      {/* Business Details (Centered) */}
      <Box sx={{ textAlign: 'center', mb: 1 }}>
        <Typography sx={{ fontWeight: 'bold', fontSize: '12px' }}>
          {businessDetails.name}
        </Typography>
        <Typography sx={{ fontSize: '10px' }}>
          {businessDetails.address}
        </Typography>
        <Typography sx={{ fontSize: '10px' }}>
          Phone: {businessDetails.phone}
        </Typography>
        <Typography sx={{ fontSize: '10px' }}>
          Email: {businessDetails.email}
        </Typography>
        {businessDetails.gstin && (
          <Typography sx={{ fontSize: '10px' }}>
            GSTN: {businessDetails.gstin}
          </Typography>
        )}
      </Box>

      <Divider sx={{ border: '1px dashed #bbb', my: 1 }} />

      {/* Invoice Details */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontSize: '10px', fontWeight: 'bold' }}>
          Invoice #: {invoiceDetails.invoiceNumber}
        </Typography>
        <Typography sx={{ fontSize: '10px', fontWeight: 'bold' }}>
          Date: {formatDate(invoiceDetails.invoiceDate)}
        </Typography>
      </Box>

      {/* Client Details (Left aligned) */}
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontWeight: 'bold', fontSize: '10px' }}>
          Bill To:
        </Typography>
        <Typography sx={{ fontWeight: 'bold', fontSize: '10px' }}>
          {clientDetails.name}
        </Typography>
        <Typography sx={{ fontSize: '10px' }}>
          {clientDetails.address}
        </Typography>
        {clientDetails.phone && (
          <Typography sx={{ fontSize: '10px' }}>
            Phone: {clientDetails.phone}
          </Typography>
        )}
        {clientDetails.email && (
          <Typography sx={{ fontSize: '10px' }}>
            Email: {clientDetails.email}
          </Typography>
        )}
        {clientDetails.gstin && (
          <Typography sx={{ fontSize: '10px' }}>
            GSTN: {clientDetails.gstin}
          </Typography>
        )}
      </Box>

      <Divider sx={{ border: '1px dashed #bbb', my: 1 }} />

      {/* Items Table */}
      <TableContainer sx={{ mb: 1 }}>
        <Table size="small" padding="none" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <StyledTableHeadRow>
              <DescriptionCell>Description</DescriptionCell>
              <QuantityCell align="right">Qty</QuantityCell>
              {!invoiceDetails.isCollectingCopy && <RateCell align="right">Rate</RateCell>}
              {!invoiceDetails.isCollectingCopy && <AmountCell align="right">Amount</AmountCell>}
            </StyledTableHeadRow>
          </TableHead>
          <TableBody>
            {invoiceDetails.items.map((item, index) => (
              <StyledTableRow key={index}>
                <DescriptionCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {item.description}
                </DescriptionCell>
                <QuantityCell align="right">{item.quantity}</QuantityCell>
                {!invoiceDetails.isCollectingCopy && <RateCell align="right">{formatCurrency(item.rate)}</RateCell>}
                {!invoiceDetails.isCollectingCopy && <AmountCell align="right">{formatCurrency(item.amount)}</AmountCell>}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!invoiceDetails.isCollectingCopy && (
        <>
          <Divider sx={{ border: '1px dashed #bbb', my: 1 }} />

          {/* Invoice Summary */}
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: '10px' }}>
              Invoice Summary
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography sx={{ fontSize: '10px' }}>Total Value:</Typography>
              <Typography sx={{ fontSize: '10px' }}>{formatCurrency(totals.subtotal)}</Typography>
            </Box>

            {totals.discount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '10px' }}>Discount:</Typography>
                <Typography sx={{ fontSize: '10px' }}>-{formatCurrency(totals.discount)}</Typography>
              </Box>
            )}

            {totals.additionalChargesTotal > 0 && Array.isArray(invoiceDetails.additionalCharges) && (
              <>
                {invoiceDetails.additionalCharges.map((charge, index) => (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '10px' }}>{charge.name}:</Typography>
                    <Typography sx={{ fontSize: '10px' }}>{formatCurrency(charge.amount)}</Typography>
                  </Box>
                ))}
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, pt: 0.5, borderTop: '1px dashed #bbb' }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '10px' }}>Total Invoice Value:</Typography>
              <Typography sx={{ fontWeight: 'bold', fontSize: '10px' }}>{formatCurrency(totals.total)}</Typography>
            </Box>
          </Box>

          {/* Composition Scheme Notice */}
          <Box sx={{ mb: 1, border: '1px dashed #bbb', p: 0.5 }}>
            <Typography sx={{ fontSize: '9px', fontStyle: 'italic', textAlign: 'center' }}>
              We are registered in composition scheme, hence no tax should be collected
            </Typography>
          </Box>

          {/* Terms & Conditions */}
          {invoiceDetails.termsAndConditions && (
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '10px' }}>
                Terms & Conditions:
              </Typography>
              {Array.isArray(invoiceDetails.termsAndConditions) ? (
                invoiceDetails.termsAndConditions.map((term, index) => (
                  <Typography key={index} sx={{ fontSize: '9px' }}>
                    {index + 1}. {term}
                  </Typography>
                ))
              ) : (
                <Typography sx={{ fontSize: '9px' }}>
                  {invoiceDetails.termsAndConditions}
                </Typography>
              )}
            </Box>
          )}

          <Divider sx={{ border: '1px dashed #bbb', my: 1 }} />
        </>
      )}

      {/* Thank You Message */}
      <Box sx={{ textAlign: 'center', mb: 1 }}>
        <Typography sx={{ fontWeight: 'bold', fontSize: '10px' }}>
          Thank you for your business!
        </Typography>
      </Box>
    </ThermalPaper>
  );
};

export default ThermalInvoiceFormatComposition; 